import type {
  PropertyClaim,
  Floater,
  Insured,
  Location,
  OilTank,
  PriorAddress,
  Watercraft,
  Trailer,
} from 'api';
import { PROVINCE_ID_TO_CODE } from 'variables/constants';

export const MAX_CO_INSUREDS = 4;
export const MAX_PRIOR_ADDRESSES = 10;

export interface RiskAddress {
  address: string;
  unit_number?: string;
  city: string;
  province: string;
  postal_code: string;
}

export const getRiskAddress = (location: Location): RiskAddress => ({
  address: location.address,
  unit_number: location.unit_number,
  city: location.city,
  province: location.province,
  postal_code: location.postal_code,
});

export const formatAddress = (riskAddress: RiskAddress): string => {
  return [
    riskAddress.address,
    riskAddress.city,
    riskAddress.province,
    riskAddress.postal_code,
  ]
    .filter(Boolean)
    .join(', ');
};

export const createEmptyPriorAddress = (id: number = 1): PriorAddress => ({
  id,
  address: '',
  unit_number: '',
  city: '',
  province: '',
  postal_code: '',
});

export const createEmptyInsured = (): Insured => ({
  is_corporation: false,
  retrieve_credit: '',
  privacy_consent: '',
  relation_to_insured: '',
  other_relation_to_insured: '',
  salutation: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  suffix: '',
  date_of_birth: null,
  home_phone: '',
  applicant_gender: '',
  occupation: '',
  other_occupation: '',
  employment_status: '',
  date_hired: null,
  mailing_address: '',
  mailing_unit_number: '',
  mailing_city: '',
  mailing_province: '',
  mailing_postal_code: '',
  different_mailing_address: false,
  has_applicant_changed_address_in_the_last_3_years: '',
  prior_address: '',
  prior_unit_number: '',
  prior_city: '',
  prior_province: '',
  prior_postal_code: '',
  prior_addresses: [],
});

export const createEmptyCorporationInsured = (): Insured => ({
  ...createEmptyInsured(),
  is_corporation: true,
});

export const createEmptyLocation = (
  id: number = 1,
  provinceId: number = 1,
) => ({
  id,
  location_type: '',
  floor_unit_located_on: null as number | null,
  tenant_condo_building_foundation_type: '',
  tenant_condo_exterior_wall_material_type: '',
  tenant_condo_roofing_material_type: '',
  address: '',
  unit_number: '',
  city: '',
  province: PROVINCE_ID_TO_CODE[provinceId] || 'ON',
  postal_code: '',
  number_of_mortgages: null as number | null,
  address_validated: false,
  validated_street_number: '',
  validated_street_name: '',
  validated_city: '',
  validated_province: '',
  validated_postal_code: '',
  insured: createEmptyInsured(),
  co_insureds: [] as Insured[],
  metres_to_hydrant: '',
  kilometres_to_firehall: null as number | null,
  unprotected: false,
  evaluation_date: null as string | null,
  dwelling_use: '',
  location_accessibility: '',
  other_location_accessibility: '',
  home_evaluation_tool_used: '',
  building_value: null as number | null,
  building_coverage: null as number | null,
  outbuildings: null as number | null,
  contents: null as number | null,
  construction_type: '',
  other_construction_type: '',
  exterior_wall_type: '',
  other_exterior_wall_type: '',
  property_type: '',
  other_property_type: '',
  garage_type: '',
  other_garage_type: '',
  number_of_cars: null as number | null,
  number_of_storeys: '',
  storeys: null as number | null,
  building_shape: '',
  number_of_selfcontained_units_in_property: null as number | null,
  number_of_families: null as number | null,
  number_of_full_bathrooms: null as number | null,
  number_of_half_bathrooms: null as number | null,
  number_of_kitchens: '',
  kitchens: null as number | null,
  other_location_type: '',
  eldest_occupant_birthdate: null as string | null,
  year_built_yyyy: null as number | null,
  occupancy_date: null as string | null,
  occupancy: '',
  resident_smokers: '',
  in_law_suite: false,
  first_time_homebuyer: '',
  number_of_dogs_in_the_household: null as number | null,
  breeds_of_dogs: [] as string[],
  total_living_area_sq_ft: null as number | null,
  basement_area_sq_ft: null as number | null,
  area_unit: 'sq_ft',
  building_foundation_type: '',
  building_foundation_material_type: '',
  basement_percentage: null as number | null,
  crawlspace_percentage: null as number | null,
  post_pier_on_slab_percentage: null as number | null,
  slab_concrete_slab_percentage: null as number | null,
  walkout_basement_percentage: null as number | null,
  building_foundation_type_other_percentage: null as number | null,
  other_building_foundation_type: '',
  finished_basement_percentage: null as number | null,
  burglar_alarm: '',
  fire_alarm: '',
  block_watch: false,
  walled_community: false,
  deadbolt_locks: false,
  bars_on_windows: false,
  sprinklers: false,
  number_of_smoke_detectors: null as number | null,
  number_of_fire_extinguishers: null as number | null,
  year_of_most_recent_updates_to_electrical: null as number | null,
  extent_of_updates_to_electrical: '',
  wiring_type: '',
  other_wiring_type: '',
  electrical_strength_amperes_a: null as number | null,
  electrical_panel_type: '',
  year_of_most_recent_updates_to_plumbing: null as number | null,
  extent_of_updates_to_plumbing: '',
  plumbing_material_type: '',
  plumbing_material_percentage: null as number | null,
  other_plumbing_material_type: '',
  plumbing_materials: [] as { type: string; percentage: number | null }[],
  hot_water_tank_boiler_type: '',
  other_hot_water_tank_boiler_type: '',
  hot_water_tank_boiler_year: null as number | null,
  water_main_valve_shut_off_type: '',
  number_of_water_main_valve_shut_off_sensors: null as number | null,
  septic_tank: false,
  backwater_valve_type_installed: '',
  sump_pump_type: '',
  other_sump_pump_type: '',
  alarmed_sump_pump: '',
  sump_pump_auxiliary_power: '',
  other_sump_pump_auxiliary_power: '',
  water_leak_detection_system: '',
  // Roofing fields
  year_of_most_recent_updates_to_roof: null as number | null,
  extent_of_updates_to_roof: '',
  roofing_material_type: '',
  other_roofing_material_type: '',
  impact_resistance_class_of_roofing: '',
  aggregate_percentage: null as number | null,
  exterior_wall_type_aluminum_percentage: null as number | null,
  asbestos_percentage: null as number | null,
  exterior_wall_type_brick_percentage: null as number | null,
  brick_veneer_percentage: null as number | null,
  cedar_siding_percentage: null as number | null,
  cement_percentage: null as number | null,
  cement_fibre_board_percentage: null as number | null,
  cement_fibre_shingle_percentage: null as number | null,
  exterior_wall_type_concrete_percentage: null as number | null,
  exterior_wall_type_fibreglass_percentage: null as number | null,
  log_siding_percentage: null as number | null,
  metal_siding_percentage: null as number | null,
  exterior_wall_type_monocoque_percentage: null as number | null,
  exterior_wall_type_stone_percentage: null as number | null,
  stone_veneer_percentage: null as number | null,
  stucco_percentage: null as number | null,
  vinyl_percentage: null as number | null,
  wood_exterior_percentage: null as number | null,
  exterior_wall_type_other_percentage: null as number | null,
  year_of_most_recent_updates_to_heating: null as number | null,
  extent_of_updates_to_heating: '',
  // Primary heating fields
  primary_heating_type_installed: '',
  other_primary_heating_type: '',
  primary_heating_fuel_type: '',
  other_primary_heating_fuel_type: '',
  primary_heating_approved: false,
  primary_heating_approved_by: '',
  primary_heating_professionally_installed: '',
  primary_heating_fireplace_type: '',
  second_primary_heating_fuel_type: '',
  other_second_primary_heating_fuel_type: '',
  primary_heating_wett_certified: '',
  primary_heating_wett_certification_number: '',
  primary_heating_wett_inspection_date: null as string | null,
  primary_heating_fuel_type_number_of_face_cords_per_year: null as
    | number
    | null,
  // Chimney fields
  chimney_exists: '',
  chimney_type: '',
  other_chimney_type: '',
  chimney_built_from: '',
  chimney_built_from_other: '',
  chimney_lining: '',
  chimney_lining_other: '',
  chimney_location: '',
  chimney_location_other: '',
  chimney_professionally_cleaned: '',
  frequency_of_chimney_cleaning: '',
  chimney_certified_by: '',
  chimney_certified_by_other: '',
  wett_certified: '',
  wett_certification_number: '',
  wett_inspected: '',
  wett_inspection_report_date: null as string | null,
  // Wood stove fields
  stove_location: '',
  stove_location_other: '',
  woodstove_installed_date: null as string | null,
  number_of_hours_used_per_day: null as number | null,
  number_of_days_used_per_year: null as number | null,
  // Auxiliary heating fields
  auxiliary_heating_type_installed: '',
  other_auxiliary_heating_type: '',
  auxiliary_heating_fuel_type: '',
  other_auxiliary_heating_fuel_type: '',
  auxiliary_heating_approved: false,
  auxiliary_heating_approved_by: '',
  auxiliary_heating_professionally_installed: '',
  auxiliary_heating_fireplace_type: '',
  second_auxiliary_heating_fuel_type: '',
  other_second_auxiliary_heating_fuel_type: '',
  auxiliary_heating_wett_certified: '',
  auxiliary_heating_wett_certification_number: '',
  auxiliary_heating_wett_inspection_date: null as string | null,
  auxiliary_heating_fuel_type_number_of_face_cords_per_year: null as
    | number
    | null,
  auxiliary_heating_year_installed: null as number | null,
  auxiliary_heating_percentage_renovated: null as number | null,
  auxiliary_heating_automatic_fire_extinguisher: false,
  // Oil tank fields
  number_of_oil_storage_tanks: '',
  oil_tanks: [] as OilTank[],
  claims: [] as PropertyClaim[],
  floaters: [] as Floater[],
  watercraft: [] as Watercraft[],
  trailers: [] as Trailer[],
});

export const createEmptyFloater = (id: number = 1): Floater => ({
  id,
  item_type: '',
  item_description: '',
  item_value_cad: null,
  deductible_cad: null,
  purchase_date: null,
  valuation_date: null,
  serial_identification_number: '',
  coverage_required: '',
  item_exhibited: '',
  item_used_for_business: false,
});

export const createEmptyWatercraft = (id: number = 1): Watercraft => ({
  id,
  name: '',
  manufacturer: '',
  make_model: '',
  model_year_yyyy: null,
  description: '',
  length_ft: null,
  value_cad: null,
  horsepower: null,
  max_speed_mph: null,
  purchase_date: null,
  propulsion_type: '',
  mooring_location: '',
  watercraft_usage: [],
  waters_navigated: '',
  rented_or_leased_to_others: false,
  describe_commercial_charter_use: '',
  number_of_weeks_rented_to_others_per_annum: null,
  has_trailer: false,
  trailer_value_cad: null,
  trailer_length_ft: null,
  trailer_make_model: '',
  trailer_manufacturer: '',
  trailer_model_year_yyyy: null,
  trailer_license_plate: '',
  trailer_serial_number: '',
  trailer_condition: '',
  trailer_purchase_date: null,
  trailer_is_road_licensed: false,
});

export const createEmptyTrailer = (id: number = 1): Trailer => ({
  id,
  trailer_type: '',
  other_trailer_type: '',
  manufacturer: '',
  make_model: '',
  model_year_yyyy: null,
  length_ft: null,
  replacement_value_cad: null,
  license_plate: '',
  identification_serial_number: '',
  condition_when_purchased: '',
  purchase_date: null,
  number_of_operators: null,
  is_the_trailer_road_licensed: false,
  province_territory: '',
  occupied_by_the_owner: '',
  the_trailer_is_permanently_parked: '',
  park_name_if_applicable: '',
  equipment_and_contents_value_cad: null,
  hail_coverage_required: '',
  number_of_days_outside_of_canada_per_annum: null,
  rented_or_leased_to_others: false,
  number_of_weeks_rented_to_others_per_annum: null,
});

export const createEmptyPropertyClaim = (id: number = 1): PropertyClaim => ({
  id,
  source: 'manual',
  date: null,
  type_code: null,
  cause_code: null,
  fault_percentage: 0,
  payout_amount: null,
  status: '',
});
